import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import MaterialIcon from '../ui/MaterialIcon';
import { associationService } from '../../services/associationService';
import { SEARCH_THRESHOLD, matchesAssociation } from '../../lib/associationSearch';

// Sidebar control for Atrionos staff (Super Admin): shows the association this tab
// is in and jumps to any other. Renders nothing for everyone else. Past
// SEARCH_THRESHOLD associations a search box appears (Philippe, 2026-10-30).

export function AssociationSwitcher() {
  const { isPlatformOwner } = useAuth();
  const { hoa } = useHOA();
  const [open, setOpen] = useState(false);
  const [associations, setAssociations] = useState([]);
  const [switching, setSwitching] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const loaded = useRef(false);

  useEffect(() => {
    if (!open || loaded.current) return;
    loaded.current = true;
    associationService.listAssociations().then(setAssociations, (e) => setError(e.message));
  }, [open]);

  if (!isPlatformOwner) return null;

  const searchable = associations.length > SEARCH_THRESHOLD;
  const shown = searchable ? associations.filter((a) => matchesAssociation(a, query)) : associations;

  const handleSwitch = async (slug) => {
    if (slug === hoa?.slug) { setOpen(false); return; }
    setSwitching(slug);
    setError(null);
    try {
      await associationService.switchTo(slug);
    } catch (e) {
      setError(e.message);
      setSwitching(null);
    }
  };

  return (
    <div className="mt-3 relative z-30" data-testid="association-switcher">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2 rounded-full bg-sidebar-active text-sidebar-accent text-sm font-semibold hover:brightness-110 transition-all"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 min-w-0">
          <MaterialIcon name="shield_person" size={18} />
          <span className="truncate">Super Admin · {hoa?.name || 'Association'}</span>
        </span>
        <MaterialIcon name="expand_more" size={18} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-2xl warm-tactile-shadow border border-amber-100/40 overflow-hidden">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-4 pt-3 pb-1">Switch association</p>
          {error && <p className="text-xs font-medium text-red-600 px-4 pb-2">{error}</p>}
          {!error && associations.length === 0 && (
            <p className="text-xs font-medium text-slate-500 px-4 pb-3">Loading…</p>
          )}
          {searchable && (
            <div className="px-3 pb-1">
              <input
                type="search"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && shown.length > 0 && shown[0].is_active) handleSwitch(shown[0].slug);
                  if (e.key === 'Escape') setOpen(false);
                }}
                placeholder={`Search ${associations.length} associations`}
                aria-label="Search associations"
                className="w-full rounded-full border border-amber-100/60 bg-main-bg px-4 py-2 text-sm font-medium text-primary-dark focus:outline-none focus:ring-2 focus:ring-secondary/40"
              />
            </div>
          )}
          {searchable && shown.length === 0 && (
            <p className="text-xs font-medium text-slate-500 px-4 py-3">No association matches “{query}”.</p>
          )}
          <div className="p-1.5 space-y-1 max-h-72 overflow-y-auto">
            {shown.map((a) => {
              const current = a.slug === hoa?.slug;
              return (
                <button
                  key={a.id}
                  type="button"
                  disabled={!!switching || !a.is_active}
                  onClick={() => handleSwitch(a.slug)}
                  className={`w-full text-left px-3 py-2.5 rounded-2xl transition-all ${current ? 'bg-section-bg' : 'hover:bg-main-bg'} disabled:opacity-60`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-primary-dark truncate">{a.name}</span>
                    {current && <span className="text-[10px] font-bold uppercase tracking-tighter text-secondary">Here</span>}
                    {switching === a.slug && <span className="text-[10px] font-bold uppercase tracking-tighter text-slate-500">Opening…</span>}
                  </span>
                  <span className="block text-[10px] font-bold uppercase tracking-tighter text-slate-500">
                    {[a.city, a.state].filter(Boolean).join(', ') || a.slug}
                    {a.subscription_status === 'trialing' ? ' · Trial' : ''}
                    {a.is_last_visited && !current ? ' · Last visited' : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default AssociationSwitcher;
